import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useOrg } from "../lib/org";
import { Button, ErrorState, Money, PageHeader, Spinner, StatusBadge } from "../components/ui";
import { DataTable } from "../components/DataTable";
import { formatDate } from "../lib/format";
import type { InvoiceListRow } from "../lib/models";
import { InvoicePilotLink } from './InvoicePilotLink';

export default function Invoices() {
  const { currentOrg } = useOrg();
  const navigate = useNavigate();
  const currency = currentOrg?.baseCurrency ?? "USD";

  const { data, isLoading, error } = useQuery({
    queryKey: ["invoices", currentOrg?.id],
    queryFn: () => api.get<InvoiceListRow[]>("/invoices"),
    enabled: !!currentOrg,
  });

  return (
    <div>
      <PageHeader
        title="Invoices"
        subtitle="Money owed to you by customers"
        action={<Button onClick={() => navigate("/invoices/new")}>Create invoice</Button>}
      />
      <InvoicePilotLink />
      {isLoading && <Spinner />}
      {error && <ErrorState error={error} />}
      {data && (
        <DataTable
          rows={data}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/invoices/${r.id}`)}
          columns={[
            { key: "num", header: "No.", render: (r) => r.docNumber ?? "—" },
            { key: "cust", header: "Customer", render: (r) => r.customerName },
            { key: "date", header: "Date", render: (r) => formatDate(r.txnDate) },
            { key: "due", header: "Due", render: (r) => formatDate(r.dueDate) },
            {
              key: "status",
              header: "Status",
              render: (r) => <StatusBadge status={r.status} />,
            },
            {
              key: "total",
              header: "Total",
              align: "right",
              render: (r) => <Money cents={r.total} currency={currency} />,
            },
            {
              key: "balance",
              header: "Balance",
              align: "right",
              render: (r) => <Money cents={r.balance} currency={currency} />,
            },
          ]}
        />
      )}
    </div>
  );
}
